// ===========================================
// HOOKS: Tareas para un grupo de clientes (CORE)
// ===========================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { toast } from 'sonner'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { usePermissions } from '@/hooks/usePermissions'
import { useAuditor } from '@/hooks/useAuditor'
import { assertPermiso, handleMutationError, puedeTocarTarea } from '@/lib/permisos-guard'
import type { Permisos } from '@/types/roles'
import { notificacionesService } from '../services/notificacionesService'
import { PLANTILLAS_NOTIFICACION, type NotificacionOrigen } from '../types/notificaciones'
import {
  listarClientesDeGrupo,
  contarClientesDeGrupoRecurrente,
  crearTareaParaGrupo,
  columnasDeGrupo,
  listarTareasDeGrupos,
  actualizarFechaDeTareas,
  obtenerEstadoDeTareas,
  type TipoGrupoTarea,
  type CrearTareaGrupoInput,
  type TareaConGrupo,
} from '../services/tareasGrupoService'
import { tareasKeys } from './useTareas'
import { calendarioKeys } from './useCalendario'

/**
 * Las tareas de los grupos pedidos, en cualquier estado y con las policies del usuario.
 *
 * La clave empieza con `['tareas', 'lista']`. Así el cambio de estado optimista de
 * `useCambiarEstadoTarea` también se aplica acá, y su invalidación la refresca. Se refresca cada
 * 30 segundos, igual que el tablero.
 */
export function useTareasDeGrupos(claves: string[]) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const ordenadas = [...new Set(claves)].sort()

  return useQuery({
    queryKey: [...tareasKeys.all, 'lista', 'grupos', ordenadas],
    queryFn: () => listarTareasDeGrupos(supabase!, ordenadas),
    enabled: !!supabase && !supabaseLoading && ordenadas.length > 0,
    refetchInterval: 30 * 1000,
  })
}

/**
 * Cuántas tareas va a crear el formulario para un grupo.
 *
 * - Sin repetir: los clientes activos del grupo que ve el usuario, que son los que crea
 *   `useCrearTareaParaGrupo`. Las dos llaman a `listarClientesDeGrupo`.
 * - Con repetir: los que crea el cron en cada ocurrencia, contados con sus privilegios. Para
 *   un colaborador que no ve todos los clientes, este número es mayor que el anterior.
 */
export function useContarClientesDeGrupo(
  tipo: TipoGrupoTarea | null,
  valor: string | null,
  repetir: boolean
) {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: ['tareas-grupo-conteo', organizacionId, tipo, valor, repetir],
    queryFn: async () => {
      const grupo = columnasDeGrupo(tipo, valor)
      if (repetir) return contarClientesDeGrupoRecurrente(supabase!, organizacionId!, grupo)
      const ids = await listarClientesDeGrupo(supabase!, organizacionId!, grupo)
      return ids.length
    },
    enabled: !!supabase && !!organizacionId && !!tipo && !!valor,
    staleTime: 1000 * 30,
  })
}

type DatosCrearGrupo = Omit<
  CrearTareaGrupoInput,
  'organizacion_id' | 'created_by' | 'asignado_por'
>

export function useCrearTareaParaGrupo() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { user } = useAuth0()
  const { puede } = usePermissions()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (datos: DatosCrearGrupo) => {
      assertPermiso(puede, 'crearTarea')
      if (!organizacionId) throw new Error('No hay organización activa')
      const creadoPor = (user?.email || '').toLowerCase()
      return crearTareaParaGrupo(supabase!, {
        ...datos,
        organizacion_id: organizacionId,
        created_by: creadoPor,
        asignado_por: datos.asignado_a ? creadoPor : null,
      })
    },
    onSuccess: (resultado, variables) => {
      if (resultado.creadas === 0) {
        toast.warning('Todavía ningún cliente activo entra en ese grupo.')
        return
      }

      queryClient.invalidateQueries({ queryKey: tareasKeys.all })
      // Las tareas del grupo también se muestran en el calendario unificado.
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
      toast.success(
        resultado.creadas === 1
          ? 'Tarea creada para 1 cliente.'
          : `Se crearon ${resultado.creadas} tareas, una por cliente.`
      )

      // Una sola notificación con la cantidad, no una por tarea (mismo patrón que
      // `conceptoAsignadoEnMasa`): con 11 clientes de una vez, 11 notificaciones son ruido.
      const asignadoA = variables.asignado_a
      if (
        supabase &&
        asignadoA &&
        user?.email &&
        asignadoA.toLowerCase() !== user.email.toLowerCase()
      ) {
        const plantilla = PLANTILLAS_NOTIFICACION.tareaAsignadaEnMasa(variables.titulo, resultado.creadas)
        const origen: NotificacionOrigen = {
          canal: 'web',
          actor_email: user.email,
          tabla_fuente: 'tareas',
        }
        notificacionesService
          .notificar(supabase, {
            destinatarios: [`user:${asignadoA}`],
            titulo: plantilla.titulo,
            mensaje: plantilla.mensaje,
            tipo: plantilla.tipo,
            enlace: '/tareas',
            origen,
          })
          .catch((err) => console.error('Error notificando la tarea de grupo:', err))
      }
    },
    onError: (error) => handleMutationError(error, 'No se pudieron crear las tareas del grupo'),
  })
}

/** Las tareas del grupo a las que el usuario le puede mover la fecha: sin completar y con permiso. */
export const tareasConFechaMovible = (
  tareas: TareaConGrupo[],
  puede: Partial<Permisos>,
  emailUsuario: string | null | undefined
): TareaConGrupo[] =>
  tareas.filter((t) => t.estado !== 'completada' && puedeTocarTarea(t, puede, emailUsuario))

interface ActualizarFechaGrupoInput {
  /** Las tareas del grupo que ve el usuario, en cualquier estado. */
  tareas: TareaConGrupo[]
  fecha: string | null
}

/**
 * Pone la misma fecha límite a las tareas de un grupo. Decide sobre el estado actual de cada tarea
 * en la base, no sobre el que muestra la pantalla. Deja afuera las completadas y las que el usuario
 * no puede editar. Avisa una vez a cada responsable de las tareas movidas, salvo a quien hizo el
 * cambio. Si falla un lote, igual registra las tareas que se movieron y muestra el error.
 */
export function useActualizarFechaGrupo() {
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { puede, emailUsuario } = usePermissions()
  const { editar } = useAuditor()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ tareas, fecha }: ActualizarFechaGrupoInput) => {
      if (!puede.editarTarea) assertPermiso(puede, 'editarTareaPropia')
      const actuales = await obtenerEstadoDeTareas(supabase!, tareas.map((t) => t.id))
      const pendientes = actuales.filter((t) => t.estado !== 'completada' && t.fecha_limite !== fecha)
      const aMover = pendientes.filter((t) => puedeTocarTarea(t, puede, emailUsuario))
      const { actualizadas, error } = await actualizarFechaDeTareas(
        supabase!,
        aMover.map((t) => t.id),
        fecha
      )
      return {
        actualizadas,
        sinPermiso: pendientes.length - aMover.length,
        sinMover: error ? 0 : aMover.length - actualizadas.length,
        error,
      }
    },
    onSuccess: ({ actualizadas, sinPermiso, sinMover, error }, { fecha }) => {
      queryClient.invalidateQueries({ queryKey: tareasKeys.all })
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
      actualizadas.forEach((t) => editar('tarea', t.id, { cambios: { fecha_limite: fecha } }))

      const avisos = [`Fecha actualizada en ${actualizadas.length} ${actualizadas.length === 1 ? 'tarea' : 'tareas'}.`]
      if (sinPermiso === 1) avisos.push('1 tarea no se movió porque no tenés permiso para editarla.')
      if (sinPermiso > 1) avisos.push(`${sinPermiso} tareas no se movieron porque no tenés permiso para editarlas.`)
      if (sinMover === 1) avisos.push('1 tarea no se movió porque cambió mientras tanto o quedó fuera de tu alcance.')
      if (sinMover > 1) avisos.push(`${sinMover} tareas no se movieron porque cambiaron mientras tanto o quedaron fuera de tu alcance.`)
      if (error) handleMutationError(error, 'No se pudo cambiar la fecha de todas las tareas del grupo')
      else if (avisos.length > 1) toast.warning(avisos.join(' '))
      else if (actualizadas.length > 0) toast.success(avisos[0])

      if (!supabase || !user?.email) return
      const actor = user.email.toLowerCase()
      const autor = user.name || user.email
      const porResponsable = new Map<string, string>()
      for (const t of actualizadas) {
        const responsable = t.asignado_a?.toLowerCase()
        if (responsable && responsable !== actor && !porResponsable.has(responsable)) {
          porResponsable.set(responsable, t.titulo)
        }
      }
      porResponsable.forEach((titulo, responsable) => {
        const plantilla = PLANTILLAS_NOTIFICACION.tareaCambioFechaLimite(titulo, fecha, autor)
        const origen: NotificacionOrigen = { canal: 'web', actor_email: user.email!, tabla_fuente: 'tareas' }
        notificacionesService
          .notificar(supabase, {
            destinatarios: [`user:${responsable}`],
            titulo: plantilla.titulo,
            mensaje: plantilla.mensaje,
            tipo: plantilla.tipo,
            enlace: '/tareas',
            origen,
          })
          .catch((err) => console.error('Error notificando el cambio de fecha del grupo:', err))
      })
    },
    onError: (error) => handleMutationError(error, 'No se pudo cambiar la fecha del grupo'),
  })
}
